import React, { useState, useEffect } from 'react';

const SubestadosHierarchyEditor = ({
  hierarchy = [],
  availableColumns = [],
  baseTable = '',
  onUpdate,
  onError
}) => {
  const [visualMode] = useState(true); // Siempre visual cuando se usa este componente
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({
    level: 1,
    key: '',
    label: '',
    parentKey: '',
    operation: 'count', // count | sum
    column: '',
    value: '',
    operator: 'equals',
    sumColumn: ''
  });
  const [formError, setFormError] = useState('');

  // Generar key automáticamente desde label
  useEffect(() => {
    if (formData.label && !formData.key && editingIndex === null) {
      const autoKey = formData.label
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
      setFormData(prev => ({ ...prev, key: autoKey }));
    }
  }, [formData.label, editingIndex]);

  // Obtener niveles padre disponibles según el nivel seleccionado
  const getAvailableParents = (level) => {
    if (level === 1) return [];
    return hierarchy
      .filter((item, idx) => {
        if (editingIndex !== null && idx === editingIndex) return false;
        return item.level < level;
      })
      .map(item => ({ key: item.key, label: item.label, level: item.level }));
  };

  // Construir árbol visual
  const buildTree = () => {
    const tree = [];
    const level1 = hierarchy.filter(item => item.level === 1);
    
    level1.forEach(item1 => {
      const children2 = hierarchy.filter(item => item.level === 2 && item.parentKey === item1.key);
      const node1 = {
        ...item1,
        index: hierarchy.findIndex(h => h.key === item1.key),
        children: children2.map(item2 => {
          const children3 = hierarchy.filter(item => item.level === 3 && item.parentKey === item2.key);
          return {
            ...item2,
            index: hierarchy.findIndex(h => h.key === item2.key),
            children: children3.map(item3 => ({
              ...item3,
              index: hierarchy.findIndex(h => h.key === item3.key)
            }))
          };
        })
      };
      tree.push(node1);
    });
    
    return tree;
  };

  // Validar formulario
  const validateForm = () => {
    const { level, key, label, parentKey, column, value, operation, sumColumn } = formData;
    
    if (!label.trim()) {
      setFormError('La etiqueta es requerida');
      return false;
    }
    if (!key.trim()) {
      setFormError('El identificador (key) es requerido');
      return false;
    }
    if (level > 1 && !parentKey.trim()) {
      setFormError('Debes seleccionar un nivel padre para niveles 2 y 3');
      return false;
    }

    const op = String(operation || 'count').toLowerCase().trim();
    if (op === 'sum') {
      if (!String(sumColumn || '').trim()) {
        setFormError('Para operación SUMA debes indicar la columna a sumar');
        return false;
      }
      // Para SUMA, el filtro (column/value) es opcional, pero si defines columna debes definir valor.
      if (String(column || '').trim() && !String(value || '').trim()) {
        setFormError('Si defines una columna de filtro, también debes indicar el valor');
        return false;
      }
    } else {
      // Default COUNT: requiere columna + valor
      if (!String(column || '').trim()) {
        setFormError('Debes seleccionar una columna');
        return false;
      }
      if (!String(value || '').trim()) {
        setFormError('El valor es requerido');
        return false;
      }
    }
    
    // Validar que el key no esté duplicado
    const existingIndex = hierarchy.findIndex((item, idx) => 
      item.key === key.trim() && idx !== editingIndex
    );
    if (existingIndex !== -1) {
      setFormError('Este identificador ya existe. Usa uno diferente.');
      return false;
    }
    
    setFormError('');
    return true;
  };

  // Guardar nivel
  const handleSave = () => {
    if (!validateForm()) return;

    const op = String(formData.operation || 'count').toLowerCase().trim();
    const newItem = {
      level: formData.level,
      key: formData.key.trim(),
      label: formData.label.trim(),
      operation: op === 'sum' ? 'sum' : 'count',
      operator: formData.operator
    };

    // Para COUNT siempre guardamos filtro; para SUMA guardamos filtro solo si está completo
    if (op !== 'sum' || (String(formData.column || '').trim() && String(formData.value || '').trim())) {
      newItem.column = formData.column;
      newItem.value = (formData.value || '').trim();
    }

    if (op === 'sum') {
      newItem.sumColumn = String(formData.sumColumn || '').trim();
    }

    if (formData.level > 1) {
      newItem.parentKey = formData.parentKey;
    }

    let updatedHierarchy;
    if (editingIndex !== null) {
      updatedHierarchy = [...hierarchy];
      updatedHierarchy[editingIndex] = newItem;
    } else {
      updatedHierarchy = [...hierarchy, newItem];
    }

    onUpdate(updatedHierarchy);
    
    // Resetear formulario
    setFormData({
      level: 1,
      key: '',
      label: '',
      parentKey: '',
      operation: 'count',
      column: '',
      value: '',
      operator: 'equals',
      sumColumn: ''
    });
    setEditingIndex(null);
    setFormError('');
  };

  // Editar nivel
  const handleEdit = (index) => {
    const item = hierarchy[index];
    if (!item) return;

    setFormData({
      level: item.level || 1,
      key: item.key || '',
      label: item.label || '',
      parentKey: item.parentKey || '',
      operation: item.operation || item.op || 'count',
      column: item.column || '',
      value: item.value || '',
      operator: item.operator || 'equals',
      sumColumn: item.sumColumn || item.sum_column || ''
    });
    setEditingIndex(index);
    setFormError('');
  };

  // Eliminar nivel
  const handleDelete = (index) => {
    if (!window.confirm('¿Estás seguro de eliminar este nivel? Esto también eliminará todos sus hijos.')) {
      return;
    }

    const itemToDelete = hierarchy[index];
    if (!itemToDelete) return;

    // Eliminar el item y todos sus hijos
    const updatedHierarchy = hierarchy.filter((item, idx) => {
      if (idx === index) return false;
      // Eliminar hijos directos
      if (item.parentKey === itemToDelete.key) return false;
      // Eliminar hijos indirectos (nivel 3 hijos de nivel 2 que era hijo del eliminado)
      if (item.level === 3 && hierarchy.find(p => p.key === item.parentKey && p.parentKey === itemToDelete.key)) {
        return false;
      }
      return true;
    });

    onUpdate(updatedHierarchy);

    if (editingIndex === index) {
      setEditingIndex(null);
      setFormData({
        level: 1,
        key: '',
        label: '',
        parentKey: '',
        operation: 'count',
        column: '',
        value: '',
        operator: 'equals',
        sumColumn: ''
      });
    }
  };

  // Cancelar edición
  const handleCancel = () => {
    setEditingIndex(null);
    setFormData({
      level: 1,
      key: '',
      label: '',
      parentKey: '',
      operation: 'count',
      column: '',
      value: '',
      operator: 'equals',
      sumColumn: ''
    });
    setFormError('');
  };

  // Renderizar nodo del árbol
  const renderTreeNode = (node, depth = 0) => {
    const indent = depth * 24;
    const levelColors = {
      1: '#3b82f6',
      2: '#8b5cf6',
      3: '#ec4899'
    };
    const levelLabels = {
      1: 'Nivel 1',
      2: 'Nivel 2',
      3: 'Nivel 3'
    };

    return (
      <div key={node.key} style={{ marginLeft: `${indent}px`, marginBottom: '8px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px',
          background: 'rgba(13, 20, 40, 0.5)',
          borderRadius: '8px',
          border: `2px solid ${levelColors[node.level] || '#6b7280'}`,
          transition: 'all 0.2s ease'
        }}>
          <div style={{
            minWidth: '60px',
            padding: '4px 8px',
            background: levelColors[node.level] || '#6b7280',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '600',
            color: 'white',
            textAlign: 'center'
          }}>
            {levelLabels[node.level] || `Nivel ${node.level}`}
          </div>
          
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '600', color: 'var(--text)', marginBottom: '4px' }}>
              {node.label}
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
              <span style={{ marginRight: '12px' }}>⚙️ Operación: <strong>{(node.operation || 'count').toUpperCase()}</strong></span>
              {(node.operation || 'count') === 'sum' ? (
                <span style={{ marginRight: '12px' }}>∑ Sumar: <strong>{node.sumColumn || node.sum_column || '-'}</strong></span>
              ) : (
                <>
                  <span style={{ marginRight: '12px' }}>📊 Columna: <strong>{node.column}</strong></span>
                  <span style={{ marginRight: '12px' }}>🔍 Valor: <strong>{node.value}</strong></span>
                  <span>🧩 Operador: <strong>{node.operator}</strong></span>
                </>
              )}
            </div>
            {node.parentKey && (
              <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px' }}>
                👆 Padre: {hierarchy.find(h => h.key === node.parentKey)?.label || node.parentKey}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => handleEdit(node.index)}
              style={{
                padding: '6px 12px',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
            >
              ✏️ Editar
            </button>
            <button
              onClick={() => handleDelete(node.index)}
              style={{
                padding: '6px 12px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
            >
              🗑️ Eliminar
            </button>
          </div>
        </div>

        {node.children && node.children.length > 0 && (
          <div style={{ marginTop: '8px' }}>
            {node.children.map(child => renderTreeNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const tree = buildTree();
  const availableParents = getAvailableParents(formData.level);

  return (
    <div>
      {visualMode ? (
        <div>
          {/* Vista de árbol */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text)', marginBottom: '12px' }}>
              📊 Vista de Jerarquía ({hierarchy.length} niveles configurados)
            </h3>
            {tree.length === 0 ? (
              <div style={{
                padding: '24px',
                textAlign: 'center',
                background: 'rgba(13, 20, 40, 0.3)',
                borderRadius: '8px',
                border: '2px dashed rgba(96, 120, 170, 0.35)',
                color: 'rgba(255, 255, 255, 0.6)'
              }}>
                No hay niveles configurados. Agrega el primer nivel usando el formulario de abajo.
              </div>
            ) : (
              <div>
                {tree.map(node => renderTreeNode(node))}
              </div>
            )}
          </div>

          {/* Formulario */}
          <div style={{
            padding: '20px',
            background: 'rgba(13, 20, 40, 0.5)',
            borderRadius: '12px',
            border: '1px solid rgba(96, 120, 170, 0.35)'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text)', marginBottom: '16px' }}>
              {editingIndex !== null ? '✏️ Editar Nivel' : '➕ Agregar Nuevo Nivel'}
            </h3>

            {formError && (
              <div style={{
                marginBottom: '16px',
                padding: '12px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '6px',
                color: '#ef4444',
                fontSize: '13px'
              }}>
                ⚠️ {formError}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: 'var(--text)' }}>
                  Nivel *
                </label>
                <select
                  value={formData.level}
                  onChange={(e) => {
                    const newLevel = parseInt(e.target.value);
                    setFormData(prev => ({
                      ...prev,
                      level: newLevel,
                      parentKey: newLevel === 1 ? '' : prev.parentKey
                    }));
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid rgba(96, 120, 170, 0.35)',
                    background: 'rgba(13, 20, 40, 0.7)',
                    color: 'var(--text)',
                    fontSize: '14px'
                  }}
                >
                  <option value={1}>Nivel 1 (Raíz)</option>
                  <option value={2}>Nivel 2 (Hijo de Nivel 1)</option>
                  <option value={3}>Nivel 3 (Hijo de Nivel 2)</option>
                </select>
              </div>

              {formData.level > 1 && (
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: 'var(--text)' }}>
                    Nivel Padre *
                  </label>
                  <select
                    value={formData.parentKey}
                    onChange={(e) => setFormData(prev => ({ ...prev, parentKey: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid rgba(96, 120, 170, 0.35)',
                      background: 'rgba(13, 20, 40, 0.7)',
                      color: 'var(--text)',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Selecciona un nivel padre...</option>
                    {availableParents.map(parent => (
                      <option key={parent.key} value={parent.key}>
                        {parent.label} (Nivel {parent.level})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: 'var(--text)' }}>
                  Etiqueta (Label) *
                </label>
                <input
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                  placeholder="Ej: Contactabilidad: SÍ"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid rgba(96, 120, 170, 0.35)',
                    background: 'rgba(13, 20, 40, 0.7)',
                    color: 'var(--text)',
                    fontSize: '14px'
                  }}
                />
                <small style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px', display: 'block' }}>
                  Texto que verá el cliente en el dashboard
                </small>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: 'var(--text)' }}>
                  Identificador (Key) *
                </label>
                <input
                  type="text"
                  value={formData.key}
                  onChange={(e) => setFormData(prev => ({ ...prev, key: e.target.value }))}
                  placeholder="Ej: contactabilidad_si"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid rgba(96, 120, 170, 0.35)',
                    background: 'rgba(13, 20, 40, 0.7)',
                    color: 'var(--text)',
                    fontSize: '14px'
                  }}
                />
                <small style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px', display: 'block' }}>
                  Se genera automáticamente desde la etiqueta (sin espacios, usar guiones bajos)
                </small>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: 'var(--text)' }}>
                  Columna *
                </label>
                {String(formData.operation || 'count').toLowerCase().trim() === 'sum' && (
                  <small style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '6px', display: 'block' }}>
                    (Opcional) Columna de filtro. Si la dejas vacía, la suma se hará sobre el total del padre.
                  </small>
                )}
                <select
                  value={formData.column}
                  onChange={(e) => setFormData(prev => ({ ...prev, column: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid rgba(96, 120, 170, 0.35)',
                    background: 'rgba(13, 20, 40, 0.7)',
                    color: 'var(--text)',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Selecciona una columna...</option>
                  {availableColumns.length === 0 ? (
                    <option value="" disabled>
                      {baseTable ? `No hay columnas disponibles en ${baseTable}` : 'Selecciona una tabla primero'}
                    </option>
                  ) : (
                    availableColumns.map(col => (
                      <option key={col} value={col}>
                        {col}
                      </option>
                    ))
                  )}
                </select>
                <small style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px', display: 'block' }}>
                  {baseTable ? `Columnas disponibles en la tabla: ${baseTable}` : 'Selecciona una tabla en el paso "Datos" primero'}
                </small>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: 'var(--text)' }}>
                  Operación *
                </label>
                <select
                  value={formData.operation}
                  onChange={(e) => {
                    const nextOp = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      operation: nextOp,
                      // Al pasar a count, aseguramos que sumColumn no quede “a medias”
                      sumColumn: nextOp === 'sum' ? prev.sumColumn : '',
                    }));
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid rgba(96, 120, 170, 0.35)',
                    background: 'rgba(13, 20, 40, 0.7)',
                    color: 'var(--text)',
                    fontSize: '14px'
                  }}
                >
                  <option value="count">Contar (COUNT)</option>
                  <option value="sum">Suma (SUM)</option>
                </select>
              </div>
            </div>

            {String(formData.operation || 'count').toLowerCase().trim() === 'sum' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: 'var(--text)' }}>
                  Columna a sumar (SUM) *
                </label>
                <input
                  list="subestados-sum-columns"
                  type="text"
                  value={formData.sumColumn}
                  onChange={(e) => setFormData(prev => ({ ...prev, sumColumn: e.target.value }))}
                  placeholder="Ej: total_pagado"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid rgba(96, 120, 170, 0.35)',
                    background: 'rgba(13, 20, 40, 0.7)',
                    color: 'var(--text)',
                    fontSize: '14px'
                  }}
                />
                <datalist id="subestados-sum-columns">
                  {(availableColumns || []).map((col) => (
                    <option key={col} value={col} />
                  ))}
                </datalist>
                <small style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px', display: 'block' }}>
                  Columna numérica que se sumará para las filas que cumplan los filtros del padre (y del filtro opcional de este nivel).
                </small>
              </div>
            )}

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: 'var(--text)' }}>
                Operador (filtro)
              </label>
              <select
                value={formData.operator}
                onChange={(e) => setFormData(prev => ({ ...prev, operator: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid rgba(96, 120, 170, 0.35)',
                  background: 'rgba(13, 20, 40, 0.7)',
                  color: 'var(--text)',
                  fontSize: '14px'
                }}
              >
                <option value="equals">Equals (Valor exacto)</option>
                <option value="contains">Contains (Contiene texto)</option>
                <option value="in">In (Lista de valores)</option>
              </select>
              <small style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px', display: 'block' }}>
                Operador que se usa para comparar el valor del filtro (si usas filtro)
              </small>
            </div>

            <div style={{ marginTop: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: 'var(--text)' }}>
                Valor {String(formData.operation || 'count').toLowerCase().trim() === 'sum' ? '(filtro opcional)' : '*'}
              </label>
              <input
                type="text"
                value={formData.value}
                onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                placeholder="Ej: SI, NO, Interesado..."
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid rgba(96, 120, 170, 0.35)',
                  background: 'rgba(13, 20, 40, 0.7)',
                  color: 'var(--text)',
                  fontSize: '14px'
                }}
              />
              <small style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px', display: 'block' }}>
                {String(formData.operation || 'count').toLowerCase().trim() === 'sum'
                  ? 'Si quieres limitar la suma a un subestado/valor específico, completa Columna + Valor. Si no, deja la Columna vacía.'
                  : 'Valor exacto que debe tener la columna en la base de datos'}
              </small>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button
                onClick={handleSave}
                style={{
                  padding: '12px 24px',
                  background: '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  flex: 1
                }}
              >
                {editingIndex !== null ? '💾 Guardar Cambios' : '➕ Agregar Nivel'}
              </button>
              {editingIndex !== null && (
                <button
                  onClick={handleCancel}
                  style={{
                    padding: '12px 24px',
                    background: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  ❌ Cancelar
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div>
          {/* Modo JSON avanzado - se manejará desde AdminConsole */}
          <div style={{
            padding: '16px',
            background: 'rgba(13, 20, 40, 0.3)',
            borderRadius: '8px',
            border: '1px solid rgba(96, 120, 170, 0.35)',
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            El modo JSON avanzado se maneja desde el componente padre (AdminConsole).
            Cambia a modo Visual para usar el editor visual.
          </div>
        </div>
      )}
    </div>
  );
};

export default SubestadosHierarchyEditor;
